/**
 * Geolocation and markets operations for location redirect popup
 * @module location-redirect-popup/geolocation
 */

import { SELECTORS, LOCALE_PATTERN } from './constants.js';

export interface Market {
  iso_code: string;
  name: string;
  displayName?: string;
}

export interface MarketsData {
  markets: Market[];
}

export interface BrowsingContextSuggestion {
  detected_values?: {
    country?: {
      handle?: string;
      name?: string;
    };
  };
}

export interface WelcomeStrings extends Record<string, unknown> {
  subtitle: string;
  currency_note: string;
  shipping_note: string;
  go_to_site: string;
  continue_to_site: string;
}

/**
 * Replaces {key} placeholders in a template string with values from a map
 * @param {string} template - String with {key} placeholders
 * @param {Object<string, string>} values - Key-value pairs for replacement
 * @returns {string} Interpolated string
 */
export function interpolate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, function (match: string, key: string) {
    return key in values ? values[key] : match;
  });
}

/**
 * Parses translated string templates from a JSON script tag in the DOM.
 * Decodes HTML entities that Liquid's | t filter introduces (e.g. &#39; → ').
 * @param {string} selector - CSS selector for the script element
 * @param {string} label - Human-readable label for log messages
 * @returns {Object|null} Parsed strings or null if not found/invalid
 */
export function parseStringsFromScript(
  selector: string,
  label: string,
): Record<string, unknown> | null {
  const script = document.querySelector<HTMLScriptElement>(selector);
  if (!script) {
    console.warn('[Location Redirect] ' + label + ' strings script not found');
    return null;
  }
  try {
    const data = JSON.parse(script.textContent!) as unknown as Record<string, unknown>;
    const textarea = document.createElement('textarea');
    Object.keys(data).forEach(function (key) {
      if (typeof data[key] === 'string') {
        textarea.innerHTML = data[key];
        data[key] = textarea.value;
      }
    });
    return data;
  } catch (e) {
    console.error('[Location Redirect] Failed to parse ' + label + ' strings:', e);
    return null;
  }
}

/**
 * Parses welcome strings data from JSON script tag in the DOM
 * @returns {Object|null} Parsed welcome strings or null if not found/invalid
 */
export function parseWelcomeStrings(): WelcomeStrings | null {
  return parseStringsFromScript(SELECTORS.welcomeStrings, 'Welcome') as WelcomeStrings | null;
}

/**
 * @typedef {Object} Market
 * @property {string} iso_code - Country ISO code (e.g., 'US', 'CA')
 * @property {string} name - Market name
 * @property {string} [displayName] - Display name (may be set from detected values)
 */

/**
 * @typedef {Object} MarketsData
 * @property {Market[]} markets - Array of available markets
 */

/**
 * @typedef {Object} DetectedCountry
 * @property {string} handle - Country code handle
 * @property {string} name - Country display name
 */

/**
 * @typedef {Object} BrowsingContextSuggestion
 * @property {Object} detected_values - Detected user values
 * @property {DetectedCountry} detected_values.country - Detected country info
 */

/**
 * Parses markets data from JSON script tag in the DOM
 * @returns {MarketsData|null} Parsed markets data or null if not found/invalid
 */
export function parseMarketsData(): MarketsData | null {
  const script = document.querySelector<HTMLScriptElement>(SELECTORS.markets);
  if (!script) {
    console.warn('[Location Redirect] Markets data script not found');
    return null;
  }
  try {
    return JSON.parse(script.textContent!) as unknown as MarketsData;
  } catch (e) {
    console.error('[Location Redirect] Failed to parse markets data:', e);
    return null;
  }
}

/**
 * Detects user's country using Shopify's browsing_context_suggestions.json API
 * @param {string} currentCountry - Current site's country code to exclude
 * @returns {Promise<BrowsingContextSuggestion|null>} Suggestion data or null on error
 */
export async function detectCountry(
  currentCountry: string,
): Promise<BrowsingContextSuggestion | null> {
  try {
    const root = window.Shopify?.routes?.root || '/';
    const country = window.Shopify?.country || currentCountry;
    const url = `${root}browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${country}`;

    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    return (await response.json()) as unknown as BrowsingContextSuggestion;
  } catch (error) {
    console.error('[Location Redirect] Shopify geolocation API error:', error);
    return null;
  }
}

/**
 * Builds redirect URL with detected locale
 * @param {string} currentLocale - Current language locale (e.g., 'en')
 * @param {string} detectedIsoCode - Detected country ISO code (e.g., 'US')
 * @returns {string} Full redirect URL path with query string and hash
 */
export function buildRedirectUrl(currentLocale: string, detectedIsoCode: string): string {
  const path = window.location.pathname;
  const newLocale = `/${currentLocale.toLowerCase()}-${detectedIsoCode.toLowerCase()}`;
  const newPath = LOCALE_PATTERN.test(path)
    ? path.replace(LOCALE_PATTERN, newLocale)
    : newLocale + path;
  return newPath + window.location.search + window.location.hash;
}
